import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPortalInvoices, getPortalInvoicePaymentLink, type PortalInvoice } from '@lib/api/portal';
import { LargeTitleHeader, PullToRefresh } from '@/components/common';
import apiClient from '@/lib/api/client';

// ── Client invoices (accessibility pass) ───────────────────────────────────
// Written for people who don't love technology: one plain-language summary up
// top ("You're all paid up" / "You have 1 invoice to pay"), big amounts, big
// buttons, friendly due dates, and the e-Transfer alternative spelled out.

const fmt = (n: number) => `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string | null | undefined) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'long', day: 'numeric' }) : '';

// "Due August 5" / "Due today" / "Was due August 5" — no jargon
const dueLine = (inv: PortalInvoice) => {
  if (!inv.due_date) return null;
  const today = new Date().toISOString().slice(0, 10);
  const due = String(inv.due_date).slice(0, 10);
  if (due === today) return { text: 'Due today', urgent: true };
  if (due < today) return { text: `Was due ${fmtDate(inv.due_date)}`, urgent: true };
  return { text: `Due ${fmtDate(inv.due_date)}`, urgent: false };
};

export const ClientInvoicesPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<PortalInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState<number | null>(null);
  const [payError, setPayError] = useState('');
  // #13 – copy link state
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [copyingId, setCopyingId] = useState<number | null>(null);
  // Card payments can be switched off in Settings › Financial — hide the
  // buttons entirely so clients only see the e-Transfer path.
  const [cardPayOn, setCardPayOn] = useState(true);
  useEffect(() => {
    apiClient.get('/payments/status')
      .then(r => setCardPayOn(r.data?.data?.configured !== false))
      .catch(() => {});
  }, []);

  const loadInvoices = useCallback(async () => {
    try {
      const data = await getPortalInvoices();
      setInvoices(data);
      setError(null);
    } catch {
      setError('We couldn\'t load your invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // Returning from Stripe checkout: confirm with the backend (covers the case
  // where the webhook hasn't landed yet), then refresh the list.
  const [paidBanner, setPaidBanner] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (params.get('paid') === '1' && sessionId) {
      apiClient.get(`/payments/verify-session?session_id=${encodeURIComponent(sessionId)}`)
        .then(r => {
          if (r.data?.data?.paid) {
            setPaidBanner(`Payment of $${Number(r.data.data.amount).toLocaleString('en-CA', { minimumFractionDigits: 2 })} received — thank you! A receipt is on its way to your email.`);
            loadInvoices();
          }
        })
        .catch(() => {})
        .finally(() => window.history.replaceState({}, '', window.location.pathname));
    } else if (params.get('cancelled') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadInvoices]);

  // #12 – one-tap pay. Same-tab: window.open after an await is popup-blocked
  // on iOS Safari (audit); Stripe redirects back here with ?paid=1.
  const handlePay = async (inv: PortalInvoice) => {
    setPaying(inv.id);
    setPayError('');
    try {
      const { url } = await getPortalInvoicePaymentLink(inv.id);
      window.location.href = url;
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setPayError(ax.response?.data?.error || 'Online payment isn\'t available right now — you can pay by e-Transfer to billing@ochrepoint.ca.');
      setPaying(null);
    }
  };

  // #13 – copy payment link to clipboard (kept for folks who pay from a computer)
  const handleCopyLink = async (inv: PortalInvoice) => {
    setCopyingId(inv.id);
    try {
      const { url } = await getPortalInvoicePaymentLink(inv.id);
      await navigator.clipboard.writeText(url);
      setCopiedId(inv.id);
      setTimeout(() => setCopiedId(null), 3000);
    } catch {
      setPayError('Could not copy link.');
    } finally {
      setCopyingId(null);
    }
  };

  const handleView = (inv: PortalInvoice) => navigate(`/portal/client/invoices/${inv.id}`);

  const outstanding = invoices.filter(i => Number(i.balance_due) > 0 && i.status !== 'paid');
  const paid = invoices.filter(i => i.status === 'paid' || Number(i.balance_due) <= 0);
  const totalOwed = outstanding.reduce((s, i) => s + Number(i.balance_due || 0), 0);

  return (
    <PullToRefresh onRefresh={loadInvoices}>
      <div className="space-y-4">
        <LargeTitleHeader title="Invoices" subtitle="Pay by card here, or by e-Transfer" />

        {loading && (
          <div className="space-y-2">
            <div className="h-28 animate-pulse rounded-xl bg-gray-100" />
            <div className="h-28 animate-pulse rounded-xl bg-gray-100" />
          </div>
        )}
        {error && !loading && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-base text-red-700 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">error</span>{error}
          </div>
        )}

        {paidBanner && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-base text-green-800 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">verified</span>{paidBanner}
          </div>
        )}
        {payError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-base text-red-700">{payError}</div>
        )}

        {/* One-line summary in plain words */}
        {!loading && !error && invoices.length > 0 && (
          totalOwed > 0 ? (
            <div className="bg-[#C77D3F]/10 border border-[#C77D3F]/30 rounded-2xl p-4">
              <p className="text-base text-[#8B5A2B]">
                You have {outstanding.length === 1 ? 'one invoice' : `${outstanding.length} invoices`} to pay
              </p>
              <p className="text-3xl font-black text-[#2C1810] mt-0.5">{fmt(totalOwed)}</p>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
              <span className="material-symbols-outlined text-green-600 text-3xl">check_circle</span>
              <div>
                <p className="text-lg font-black text-green-800">You're all paid up</p>
                <p className="text-sm text-green-700">Thank you for your business!</p>
              </div>
            </div>
          )
        )}

        {!loading && !error && invoices.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <span className="material-symbols-outlined text-gray-300 text-4xl">receipt_long</span>
            <p className="text-base text-gray-500 mt-2">No invoices yet.</p>
            <p className="text-sm text-gray-400 mt-1">When we send you an invoice, it will appear here.</p>
          </div>
        ) : !loading && (
          <>
            {outstanding.map(inv => {
              const due = dueLine(inv);
              return (
                <div key={inv.id} className="bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {inv.job_title && <p className="text-sm text-gray-500">{inv.job_title}</p>}
                      <p className="text-base font-bold text-gray-900">Invoice {inv.invoice_number}</p>
                    </div>
                    {due && (
                      <span className={`shrink-0 text-sm px-3 py-1 rounded-full font-bold ${due.urgent ? 'bg-red-100 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                        {due.text}
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-500 mt-3">Amount to pay</p>
                  <p className="text-3xl font-black text-[#2C1810]">{fmt(inv.balance_due)}</p>
                  {inv.amount_paid > 0 && (
                    <p className="text-sm text-green-700 mt-0.5">You've already paid {fmt(inv.amount_paid)} of {fmt(inv.total)}.</p>
                  )}

                  {cardPayOn && (
                    <button
                      onClick={() => handlePay(inv)}
                      disabled={paying === inv.id}
                      className="mt-4 w-full flex items-center justify-center gap-2 text-base font-bold px-4 py-3.5 bg-[#C77D3F] text-white rounded-2xl hover:bg-[#b36e35] disabled:opacity-50 transition-colors active:scale-[0.99]"
                    >
                      <span className="material-symbols-outlined">credit_card</span>
                      {paying === inv.id ? 'Opening secure checkout…' : 'Pay now by card'}
                    </button>
                  )}
                  {!cardPayOn && (
                    <p className="mt-4 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3">
                      Pay by Interac e-Transfer to <strong>billing@ochrepoint.ca</strong> — reference {inv.invoice_number}.
                    </p>
                  )}
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => handleView(inv)}
                      className="flex-1 flex items-center justify-center gap-1.5 text-base font-semibold px-3 py-3 rounded-2xl border border-gray-300 text-gray-700 active:bg-gray-50"
                    >
                      <span className="material-symbols-outlined text-lg">description</span>
                      See the invoice
                    </button>
                    {/* #13 – copy payment link, for paying from another device */}
                    {cardPayOn && <button
                      onClick={() => handleCopyLink(inv)}
                      disabled={copyingId === inv.id}
                      title="Copy a payment link to pay from your computer"
                      className={`flex items-center justify-center gap-1 text-sm font-semibold px-4 py-3 rounded-2xl border transition-colors ${copiedId === inv.id ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-300 text-gray-500 active:bg-gray-50'}`}
                    >
                      <span className="material-symbols-outlined text-lg">{copiedId === inv.id ? 'check' : 'link'}</span>
                      {copiedId === inv.id ? 'Copied!' : ''}
                    </button>}
                  </div>
                </div>
              );
            })}

            {outstanding.length > 0 && (
              <p className="text-sm text-gray-500 px-1">
                Prefer Interac e-Transfer? Send it to <span className="font-semibold text-gray-700">billing@ochrepoint.ca</span> and
                include the invoice number in the message.
              </p>
            )}

            {paid.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wide pt-2">Paid & done</h2>
                {paid.map(inv => (
                  <button key={inv.id} type="button" onClick={() => handleView(inv)}
                    className="w-full text-left bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3 active:bg-[#FAF6F0]">
                    <span className="material-symbols-outlined text-green-600">check_circle</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-semibold text-gray-900">Invoice {inv.invoice_number} — {fmt(inv.total)}</p>
                      <p className="text-sm text-gray-500">
                        Paid{inv.job_title ? ` · ${inv.job_title}` : ''}{inv.invoice_date ? ` · ${fmtDate(inv.invoice_date)}` : ''}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-[#C77D3F]">View</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </PullToRefresh>
  );
};
